import type { ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";

import { fetchTab } from "@/api/hooks/tab";
import { createEnergyPlot } from "@/api/hooks/tools/energyPlot";
import {
  type EnergyPlot,
  type EnergyPlotGroup,
  normalizeEnergyPlotGroupRuns,
  transformToEnergyPlot,
} from "@/energyPlotView/types";
import { remapSiteIdForForecastTypeChange } from "@/energyPlotView/utils";
import Logger from "@mm/logging";
import { DateTime, Duration } from "luxon";
const logger = Logger.fromFilename(__filename);

export type EnergyPlotsState = {
  [id: number]: EnergyPlot;
};

const initialState: EnergyPlotsState = {};

/** Keeps persisted or incoming groups in the canonical actual/previous-run shape. */
function syncEnergyGroupInitDates(group: EnergyPlotGroup): EnergyPlotGroup {
  return normalizeEnergyPlotGroupRuns(group);
}

const energyPlotsSlice = createSlice({
  name: "plots",
  initialState: initialState as EnergyPlotsState,
  reducers: {
    addEnergyPlot(state, action: PayloadAction<EnergyPlot>) {
      state[action.payload.id] = {
        ...action.payload,
        groups: action.payload.groups.map(syncEnergyGroupInitDates),
      };
    },
    discardEnergyPlot(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    setEnergyPlotProps(state, action: PayloadAction<{ id: number; props: Partial<EnergyPlot> }>) {
      const { id, props } = action.payload;
      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      Object.assign(energyPlot, props);
    },
    setEnergyPlotType(state, action: PayloadAction<{ id: number; forecastType: string }>) {
      const { id, forecastType } = action.payload;
      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      if (forecastType !== "wlp" && forecastType !== "slp") {
        logger.error("Ignoring unsupported energy forecast type", forecastType);
        return;
      }

      const previousForecastType = energyPlot.forecastType;
      energyPlot.forecastType = forecastType;
      energyPlot.siteId = remapSiteIdForForecastTypeChange(energyPlot.siteId, previousForecastType, forecastType);
      if (energyPlot.subSiteIds) {
        energyPlot.subSiteIds = energyPlot.subSiteIds.map((siteId) =>
          remapSiteIdForForecastTypeChange(siteId, previousForecastType, forecastType),
        );
      }
    },
    setEnergyPlotTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find energy plot with id", id);
        return;
      }

      energyPlot.title = title;
    },
    setEnergyForecastPlotProps(state, action: PayloadAction<EnergyPlot>) {
      const { payload } = action;

      const energyPlot = state[payload.id];

      if (!energyPlot) {
        logger.error("Could not find energy plot with id", payload.id);
        return;
      }

      energyPlot.title = payload.title;
      energyPlot.forecastType = payload.forecastType;
      energyPlot.siteId = payload.siteId;
      energyPlot.subSiteIds = payload.subSiteIds || undefined;
    },
    addEnergyGroup(state, action: PayloadAction<{ energyPlotId: number; energyGroup: EnergyPlotGroup }>) {
      const { payload } = action;

      const energyPlotGroups = state[payload.energyPlotId]?.groups;
      energyPlotGroups.push(syncEnergyGroupInitDates(payload.energyGroup));
    },
    removeEnergyGroup(state, action: PayloadAction<{ energyPlotId: number; groupIndex: number }>) {
      const { payload } = action;

      const energyPlotGroups = state[payload.energyPlotId]?.groups;
      energyPlotGroups.splice(payload.groupIndex, 1);
    },
    setEnergyGroupProps(
      state,
      action: PayloadAction<{ energyPlotId: number; groupIndex: number; energyGroup: Partial<EnergyPlotGroup> }>,
    ) {
      const { payload } = action;

      const energyPlotGroup = state[payload.energyPlotId]?.groups[payload.groupIndex];

      if (!energyPlotGroup) {
        logger.error("Could not find energy plot group with index", payload.groupIndex);
        return;
      }

      const isModelChanged =
        payload.energyGroup.model !== undefined && payload.energyGroup.model !== energyPlotGroup.model;
      const mergedEnergyPlotGroup: EnergyPlotGroup = {
        ...energyPlotGroup,
        ...payload.energyGroup,
      };

      if (isModelChanged) {
        mergedEnergyPlotGroup.actualRun = undefined;
        mergedEnergyPlotGroup.previousRuns = undefined;
      }

      const nextEnergyPlotGroup = syncEnergyGroupInitDates(mergedEnergyPlotGroup);

      Object.assign(energyPlotGroup, nextEnergyPlotGroup);
    },
    setEnergyForecastTitleStyle(state, action: PayloadAction<{ style: ElementStyle; id: number }>) {
      const { style, id } = action.payload;

      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find energy plot with id", id);
        return;
      }

      energyPlot.titleStyle = style;
    },
    setEnergyForecastTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          referenceTime,
        },
      } = action.payload;

      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find plot with id", id);
        return;
      }

      if (!energyPlot.timeLock) {
        energyPlot.datetimeFrom = null;
        energyPlot.datetimeTo = null;

        energyPlot.datetimeFromOffset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;
        energyPlot.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      energyPlot.datetimeFrom = from;
      energyPlot.datetimeToOffset = null;

      energyPlot.datetimeTo = to;
      energyPlot.datetimeToOffset = null;
    },
    adjustAllEnergyPlotDateTimeRange(state, action: PayloadAction<{ durationMinutes: number }>) {
      const { durationMinutes } = action.payload;
      for (const plotId in state) {
        const currentPlot = state[plotId];
        const { datetimeFrom, datetimeTo } = currentPlot;

        if (currentPlot.timeLock) {
          continue;
        }

        if (datetimeFrom) {
          const newFrom = DateTime.fromISO(datetimeFrom).plus({ minute: durationMinutes });
          const newTo = datetimeTo ? DateTime.fromISO(datetimeTo).plus({ minute: durationMinutes }) : null;
          currentPlot.datetimeFrom = newFrom.toISO();
          currentPlot.datetimeTo = newTo?.toISO() || null;
        }
      }
    },
    setEnergyForecastLegendStyle(state, action: PayloadAction<{ style: ElementStyle; id: number }>) {
      const { style, id } = action.payload;

      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find energy plot with id", id);
        return;
      }

      energyPlot.legendStyle = style;
    },
    setPlotTimeSettings(
      state,
      action: PayloadAction<{ id: number; timeLock: boolean; from: string; to: string; referenceTime: string }>,
    ) {
      const { id, timeLock, from, to, referenceTime } = action.payload;

      const energyPlot = state[id];

      if (!energyPlot) {
        logger.error("Could not find energy plot with id", id);
        return;
      }

      energyPlot.timeLock = timeLock;

      if (!timeLock) {
        energyPlot.datetimeFrom = null;
        energyPlot.datetimeFromOffset = from
          ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO()
          : null;

        energyPlot.datetimeTo = null;
        energyPlot.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      energyPlot.datetimeFrom = energyPlot.datetimeFromOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(energyPlot.datetimeFromOffset)).toISO()
        : DateTime.fromISO(from).toISO();
      energyPlot.datetimeFromOffset = null;

      energyPlot.datetimeTo = energyPlot.datetimeToOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(energyPlot.datetimeToOffset)).toISO()
        : DateTime.fromISO(to).toISO();
      energyPlot.datetimeToOffset = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      for (const energyPlot of tab.energy_plots) {
        state[energyPlot.id] = transformToEnergyPlot(energyPlot);
      }
    });
    builder.addCase(createEnergyPlot.fulfilled, (state, action) => {
      const energyPlot = transformToEnergyPlot(action.payload.tool);
      state[energyPlot.id] = energyPlot;
    });
  },
});

const selectEnergyPlots = (state: RootState) => state.tabGroup.present?.energyPlots;

const makeSelectEnergyPlotsByTabId = createSelector(
  [selectEnergyPlots, (_state: RootState, tabId: number) => tabId],
  (energyPlots, tabId) => Object.values(energyPlots).filter((plot) => plot.tabId === tabId),
);

export function useEnergyPlots(tabId: number) {
  return useSelector((state: RootState) => makeSelectEnergyPlotsByTabId(state, tabId));
}

const makeSelectEnergyPlotById = createSelector(
  [selectEnergyPlots, (_state: RootState, id: number) => id],
  (energyPlots, id) => energyPlots[id],
);

export function useEnergyPlot(id: number) {
  return useSelector((state: RootState) => makeSelectEnergyPlotById(state, id));
}

export const {
  addEnergyPlot,
  setEnergyPlotProps,
  setEnergyPlotType,
  setEnergyPlotTitle,
  setEnergyForecastPlotProps,
  setEnergyForecastTitleStyle,
  discardEnergyPlot,
  setEnergyForecastTimeRange,
  setPlotTimeSettings,
  adjustAllEnergyPlotDateTimeRange,
  addEnergyGroup,
  removeEnergyGroup,
  setEnergyGroupProps,
} = energyPlotsSlice.actions;

export default energyPlotsSlice.reducer;
